// A/B kitapçık eşleme listesi (2026-10-13, kullanıcı isteği): A ve B kitapçıkları hazırlandıktan
// sonra tek Excel dosyası — A soru no · B soru no · öğrenme çıktısı. Sıra sunucunun `basilan_sira`
// yanıtından (akıllı dizgi dahil basılan sıra); eşleme public_no ile.

const _imza = (satirlar) => satirlar.map(r => Number(r.public_no)).sort((a, b) => a - b).join(',');

// Yanıttaki türün sırasını saklar; soru kümesi değiştiyse eski tür atılır (B, A'nın kümesinden değilse eşlenmez).
export function abSiraKaydet(onceki, tur, satirlar) {
  if (!Array.isArray(satirlar) || satirlar.length === 0 || !['A', 'B'].includes(tur)) return onceki;
  const imza = _imza(satirlar);
  return onceki && onceki.imza === imza ? { ...onceki, [tur]: satirlar } : { imza, [tur]: satirlar };
}

// Öğrenme çıktısı numarası: kodun ders öneki atılır ("MAT.9.1.1" → "9.1.1", "TDE10.1.1.1" → "10.1.1.1").
export function ciktiNumarasi(kod) {
  return String(kod || '').replace(/^[A-Za-zÇĞİÖŞÜçğıöşü]+\.?/, '');
}

// A sırasıyla satırlar: { test, a, b, ad } — ad numaralı: "9.1.1. Gerçek sayıların …".
export function abEslesmeSatirlari(a, b) {
  const bNo = new Map(b.map(r => [Number(r.public_no), r.no]));
  return a.map(r => {
    const no = ciktiNumarasi(r.kazanim_kodu);
    const ad = r.kazanim_adi || '';
    return {
      test: r.test || null,
      a: r.no,
      b: bNo.get(Number(r.public_no)) ?? null,
      ad: no && ad ? `${no}. ${ad}` : (ad || r.kazanim_kodu || ''),
      sistem: r.public_no == null ? null : Number(r.public_no),
    };
  });
}

// --- Asgari .xlsx yazıcı (bağımlılıksız; sıkıştırmasız zip) ---
function _utf8(str) {
  const s = unescape(encodeURIComponent(str));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i += 1) out[i] = s.charCodeAt(i);
  return out;
}

let _crcTablo = null;
function _crc32(bayt) {
  if (!_crcTablo) {
    _crcTablo = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
      let c = n;
      for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
      _crcTablo[n] = c >>> 0;
    }
  }
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bayt.length; i += 1) c = _crcTablo[(c ^ bayt[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}

export function zipUret(dosyalar) {
  const parcalar = [];
  const merkez = [];
  let ofset = 0;
  for (const { ad, veri } of dosyalar) {
    const adB = _utf8(ad);
    const crc = _crc32(veri);
    const yerel = new DataView(new ArrayBuffer(30));
    yerel.setUint32(0, 0x04034b50, true); yerel.setUint16(4, 20, true); yerel.setUint16(6, 0x0800, true);
    yerel.setUint32(14, crc, true); yerel.setUint32(18, veri.length, true); yerel.setUint32(22, veri.length, true);
    yerel.setUint16(26, adB.length, true);
    const mk = new DataView(new ArrayBuffer(46));
    mk.setUint32(0, 0x02014b50, true); mk.setUint16(4, 20, true); mk.setUint16(6, 20, true); mk.setUint16(8, 0x0800, true);
    mk.setUint32(16, crc, true); mk.setUint32(20, veri.length, true); mk.setUint32(24, veri.length, true);
    mk.setUint16(28, adB.length, true); mk.setUint32(42, ofset, true);
    parcalar.push(new Uint8Array(yerel.buffer), adB, veri);
    merkez.push(new Uint8Array(mk.buffer), adB);
    ofset += 30 + adB.length + veri.length;
  }
  const merkezBoy = merkez.reduce((t, p) => t + p.length, 0);
  const son = new DataView(new ArrayBuffer(22));
  son.setUint32(0, 0x06054b50, true); son.setUint16(8, dosyalar.length, true); son.setUint16(10, dosyalar.length, true);
  son.setUint32(12, merkezBoy, true); son.setUint32(16, ofset, true);
  const hepsi = [...parcalar, ...merkez, new Uint8Array(son.buffer)];
  const out = new Uint8Array(hepsi.reduce((t, p) => t + p.length, 0));
  let i = 0;
  for (const p of hepsi) { out.set(p, i); i += p.length; }
  return out;
}

const _xml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const _sutun = (i) => String.fromCharCode(65 + i);

// basliklar: [{ ad, genislik }]; satirlar: hücre dizileri (sayı → sayı hücresi, diğerleri metin).
export function xlsxUret(basliklar, satirlar, sayfaAdi = 'Liste') {
  const hucre = (v, r, c, stil) => {
    const ref = `${_sutun(c)}${r}`;
    const s = stil ? ' s="1"' : '';
    if (typeof v === 'number' && Number.isFinite(v)) return `<c r="${ref}"${s}><v>${v}</v></c>`;
    if (v == null || v === '') return '';
    return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${_xml(v)}</t></is></c>`;
  };
  const satir = (dizi, r, stil) => `<row r="${r}">${dizi.map((v, c) => hucre(v, r, c, stil)).join('')}</row>`;
  const sayfa = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
    + '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>'
    + `<cols>${basliklar.map((b, i) => `<col min="${i + 1}" max="${i + 1}" width="${b.genislik || 14}" customWidth="1"/>`).join('')}</cols>`
    + `<sheetData>${satir(basliklar.map(b => b.ad), 1, true)}${satirlar.map((d, i) => satir(d, i + 2, false)).join('')}</sheetData>`
    + '</worksheet>';
  const dosyalar = [
    ['[Content_Types].xml', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>'],
    ['_rels/.rels', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>'],
    ['xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${_xml(sayfaAdi.slice(0, 31))}" sheetId="1" r:id="rId1"/></sheets></workbook>`],
    ['xl/_rels/workbook.xml.rels', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>'],
    ['xl/styles.xml', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>'],
    ['xl/worksheets/sheet1.xml', sayfa],
  ];
  return zipUret(dosyalar.map(([ad, metin]) => ({ ad, veri: _utf8(metin) })));
}

// A/B eşleme Excel'i (bayt). Çok testli kitapçıkta numara test içidir → Test sütunu eklenir.
export function abEslesmeXlsx(a, b) {
  const satirlar = abEslesmeSatirlari(a, b);
  const testli = satirlar.some(r => r.test);
  const basliklar = [
    ...(testli ? [{ ad: 'Test', genislik: 24 }] : []),
    { ad: 'A Kitapçığı Soru No', genislik: 20 },
    { ad: 'B Kitapçığı Soru No', genislik: 20 },
    { ad: 'Öğrenme Çıktısı', genislik: 90 },
  ];
  return xlsxUret(basliklar, satirlar.map(r => [...(testli ? [r.test || ''] : []), r.a, r.b, r.ad]), 'A-B Eşleme');
}

// Sistemdeki soru numaraları (ayrı dosya): A no · B no · sistem no (#public_no).
export function abSistemNoXlsx(a, b) {
  const satirlar = abEslesmeSatirlari(a, b);
  const testli = satirlar.some(r => r.test);
  const basliklar = [
    ...(testli ? [{ ad: 'Test', genislik: 24 }] : []),
    { ad: 'A Kitapçığı Soru No', genislik: 20 },
    { ad: 'B Kitapçığı Soru No', genislik: 20 },
    { ad: 'Sistem Soru No', genislik: 18 },
  ];
  return xlsxUret(basliklar, satirlar.map(r => [...(testli ? [r.test || ''] : []), r.a, r.b, r.sistem]), 'Sistem Numaraları');
}
